import React, { useState } from 'react';
import { AppBar, Tabs, Tab } from '@mui/material';
import { toast } from 'react-toastify';
import StaffDetails from './StaffDetails';
import { useAuth } from "../../AuthContext";  
import configData from '../../configure';
import '../Users/UserProfileModule.css';

function StaffProfile({ selectedUser: initialUser, onUserUpdate }) {
  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [currentTab, setCurrentTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const { axios, token } = useAuth();

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    // Create a deep copy of the selectedUser for editing
    setEditedUser({
      user: {
        ...selectedUser.rawData?.user,
        addressDetails: selectedUser.rawData?.user?.addressDetails
          ? [...selectedUser.rawData.user.addressDetails]
          : [{ addressType: 'CURRENT' }],
      },
      staffInfo: {
        ...selectedUser.rawData?.staffInfo,
        responsibilities: Array.isArray(selectedUser.rawData?.staffInfo?.responsibilities)
          ? [...selectedUser.rawData.staffInfo.responsibilities]
          : [],
      },
      studentInfo: null,
      parentInfo: null,
      facultyInfo: null,
    });
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedUser(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nameParts = name.split('.');

    setEditedUser((prev) => {
      const newState = { ...prev };

      if (nameParts.length === 1) {
        newState[nameParts[0]] = value;
      } else if (nameParts.length === 2) {
        newState[nameParts[0]] = {
          ...newState[nameParts[0]],
          [nameParts[1]]: value,
        };
      } else if (nameParts.length === 3) {
        newState[nameParts[0]] = {
          ...newState[nameParts[0]],
          [nameParts[1]]: {
            ...newState[nameParts[0]][nameParts[1]],
            [nameParts[2]]: value,
          },
        };
      } else if (nameParts.length === 4 && nameParts[1] === 'addressDetails') {
        const index = parseInt(nameParts[2]);
        const field = nameParts[3];
        newState[nameParts[0]] = {
          ...newState[nameParts[0]],
          [nameParts[1]]: [
            ...newState[nameParts[0]][nameParts[1]].slice(0, index),
            {
              ...newState[nameParts[0]][nameParts[1]][index],
              [field]: value,
            },
            ...newState[nameParts[0]][nameParts[1]].slice(index + 1),
          ],
        };
      }

      // Special handling for responsibilities
      if (name === 'staffInfo.responsibilities') {
        const responsibilities = value.split(',').map((item) => item.trim()).filter((item) => item !== '');
        newState.staffInfo = {
          ...newState.staffInfo,
          responsibilities,
        };
      }

      return newState;
    });
  };

  const validateForm = () => {
    const errors = [];
    if (!editedUser?.user?.firstName) errors.push('First Name is required');
    if (!editedUser?.user?.lastName) errors.push('Last Name is required');
    if (!editedUser?.user?.email || !/\S+@\S+\.\S+/.test(editedUser.user.email)) errors.push('Valid Email is required');
    if (!editedUser?.user?.phoneNumber || !/^\d{10}$/.test(editedUser.user.phoneNumber)) errors.push('Valid 10-digit Phone Number is required');
    if (editedUser?.user?.lastFourDigitsOfAadhaarNumber && !/^\d{4}$/.test(editedUser.user.lastFourDigitsOfAadhaarNumber)) errors.push('Last Four Digits of Aadhaar must be 4 digits');
    if (!editedUser?.user?.dateOfBirth) errors.push('Date of Birth is required');
    if (!editedUser?.user?.gender) errors.push('Gender is required');
    if (!editedUser?.user?.addressDetails?.[0]?.city) errors.push('City is required');
    if (!editedUser?.staffInfo?.employeeId) errors.push('Employee ID is required');
    if (!editedUser?.staffInfo?.designation) errors.push('Designation is required');
    if (!editedUser?.staffInfo?.department) errors.push('Department is required');
    if (!editedUser?.staffInfo?.joiningDate) errors.push('Joining Date is required');
    if (!editedUser?.staffInfo?.staffContractType) errors.push('Contract Type is required');
    if (editedUser?.staffInfo?.drivingExperienceYears && !/^\d+$/.test(editedUser.staffInfo.drivingExperienceYears)) errors.push('Driving Experience Years must be a number');
    return errors;
  };

  const handleSaveClick = async () => {
    setLoading(true);

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join(', '));
      setLoading(false);
      return;
    }

    try {
      const currentTime = new Date().toISOString();
      const dob = editedUser.user.dateOfBirth ? new Date(editedUser.user.dateOfBirth).toISOString() : '';
      const joiningDate = editedUser.staffInfo.joiningDate ? new Date(editedUser.staffInfo.joiningDate).toISOString() : '';
      const drivingLicenseValidity = editedUser.staffInfo.drivingLicenseValidity ? new Date(editedUser.staffInfo.drivingLicenseValidity).toISOString() : '';

      const updateData = {
        user: {
          ...editedUser.user,
          userId: selectedUser.rawData?.user?.userId,
          orgId: selectedUser.rawData?.user?.orgId || 'default-org',
          userType: selectedUser.rawData?.user?.userType || 'STAFF',
          roleId: selectedUser.rawData?.user?.roleId || 'ROLE_STAFF',
          userStatus: selectedUser.rawData?.user?.userStatus || 'ACTIVE',
          createdBy: selectedUser.rawData?.user?.createdBy || 'system',
          createdAt: selectedUser.rawData?.user?.createdAt || currentTime,
          lastUpdatedBy: 'system',
          lastUpdatedAt: currentTime,
          email: editedUser.user.email,
          phoneNumber: editedUser.user.phoneNumber,
          lastFourDigitsOfAadhaarNumber: parseInt(editedUser.user.lastFourDigitsOfAadhaarNumber) || 0,
          firstName: editedUser.user.firstName,
          middleName: editedUser.user.middleName || 'unknown',
          lastName: editedUser.user.lastName,
          dateOfBirth: dob,
          gender: editedUser.user.gender.toUpperCase(),
          placeOfBirth: editedUser.user.placeOfBirth || 'unknown',
          bloodGroup: editedUser.user.bloodGroup || 'A_POSITIVE',
          nationality: editedUser.user.nationality || 'India',
          religion: editedUser.user.religion || 'unknown',
          category: editedUser.user.category || 'unknown',
          addressDetails: [
            {
              addressType: 'CURRENT',
              addressLine1: editedUser.user.addressDetails?.[0]?.addressLine1 || '',
              addressLine2: editedUser.user.addressDetails?.[0]?.addressLine2 || '',
              city: editedUser.user.addressDetails?.[0]?.city,
              state: editedUser.user.addressDetails?.[0]?.state || 'unknown',
              country: editedUser.user.addressDetails?.[0]?.country || 'India',
              pinCode: editedUser.user.addressDetails?.[0]?.pinCode || '000000',
              alternatePhoneNumber: editedUser.user.addressDetails?.[0]?.alternatePhoneNumber || '',
            },
          ],
        },
        staffInfo: {
          ...editedUser.staffInfo,
          userId: selectedUser.rawData?.user?.userId,
          createdBy: selectedUser.rawData?.staffInfo?.createdBy || 'system',
          createdAt: selectedUser.rawData?.staffInfo?.createdAt || currentTime,
          lastUpdatedBy: 'system',
          lastUpdatedAt: currentTime,
          employeeId: editedUser.staffInfo.employeeId,
          designation: editedUser.staffInfo.designation,
          department: editedUser.staffInfo.department,
          joiningDate: joiningDate,
          responsibilities: editedUser.staffInfo.responsibilities.length > 0 ? editedUser.staffInfo.responsibilities : ['unknown'],
          staffContractType: editedUser.staffInfo.staffContractType,
          identificationMark: editedUser.staffInfo.identificationMark || 'unknown',
          drivingLicenseNumber: editedUser.staffInfo.drivingLicenseNumber || '',
          drivingLicenseValidity: drivingLicenseValidity || '',
          drivingExperienceYears: parseInt(editedUser.staffInfo.drivingExperienceYears) || 0,
        },
        studentInfo: null,
        parentInfo: null,
        facultyInfo: null,
      };

      console.log('Sending update data:', JSON.stringify(updateData, null, 2));

      const response = await fetch(`${configData.UPDATE_USER}/${selectedUser.rawData?.user?.userId}`, {
        method: 'PUT',
        headers: {
          accept: 'application/hal+json',
          Authorization: token ,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Update successful:', result);

      const updatedUser = {
        ...selectedUser,
        firstName: editedUser.user.firstName,
        middleName: editedUser.user.middleName,
        lastName: editedUser.user.lastName,
        email: editedUser.user.email,
        phoneNumber: editedUser.user.phoneNumber,
        gender: editedUser.user.gender,
        dateOfBirth: editedUser.user.dateOfBirth,
        placeOfBirth: editedUser.user.placeOfBirth,
        bloodGroup: editedUser.user.bloodGroup,
        nationality: editedUser.user.nationality,
        religion: editedUser.user.religion,
        category: editedUser.user.category,
        lastFourDigitsOfAadhaarNumber: editedUser.user.lastFourDigitsOfAadhaarNumber,
        rawData: {
          user: result.user,
          staffInfo: result.staffInfo,
        },
      };

      setSelectedUser(updatedUser);
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }

      toast.success('Staff information updated successfully!');
      setIsEditing(false);
      setEditedUser(null);
    } catch (err) {
      console.error('Error updating staff:', err);
      toast.error(`Failed to update: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-module">
      <AppBar position="static" className="profile-tab-bar" style={{ boxShadow: 'none' }}>
        <Tabs id="customTabs" value={currentTab} onChange={handleChangeTab}>
          <Tab label="Staff Profile" />
        </Tabs>
      </AppBar>



      {currentTab === 0 && (
        <StaffDetails
          selectedUser={selectedUser}
          handleChange={handleChange}
          editedUser={editedUser}
          isEditing={isEditing}
          handleEditClick={handleEditClick}
          handleCancelClick={handleCancelClick}
          handleSaveClick={handleSaveClick}
          loading={loading}
        />
      )}
      {currentTab === 1 && (
        <div>
          <h3>Work Schedule</h3>
          {selectedUser.workSchedule ? (
            <div>Work schedule details...</div>
          ) : (
            <p>No work schedule available</p>
          )}
        </div>
      )}
      {currentTab === 2 && (
        <div className="notificationsContainer">
          <h3>Notifications</h3>
          {selectedUser.notifications && selectedUser.notifications.length > 0 ? (
            <ul className="notificationsList">
              {selectedUser.notifications.map((notification, index) => (
                <li key={index} className="notificationItem">
                  <div className="notificationHeader">
                    <span className="notificationTitle">{notification.title}</span>
                    <span className="notificationDate">{new Date(notification.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="notificationContent">{notification.message}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="noNotifications">No notifications available</p>
          )}
        </div>
      )}
      {currentTab === 3 && (
        <div>
          <h3>Attendance</h3>
          <p>Staff attendance records...</p>
        </div>
      )}
      {currentTab === 4 && (
        <div>
          <h3>Performance</h3>
          <p>Staff performance metrics...</p>
        </div>
      )}
    </div>
  );
}

export default StaffProfile;